import React from 'react';
import Module from 'Components/Core/Module';
import useBrokerageConnections from 'Components/Banking/useBrokerageConnections';
import BrokerageLoginForm from 'Components/Banking/BrokerageLoginForm';
import BrokerageOrderForm from 'Components/Banking/BrokerageOrderForm';
import './Brokerage.css';

// Muss (wie ModuleTable es intern tut) in <Module> gewrappt werden - nur das
// registriert bei x.wam[id].show()/hide() die tatsaechliche Ein-/Ausblende-
// Logik beim Seitenwechsel. Ohne diesen Wrapper bleibt die Seite dauerhaft im
// DOM sichtbar und ueberlappt beim Navigieren mit anderen Modulen.
export default function BrokerageOverview(props) {

	const x = props.x;
	const connections = useBrokerageConnections(x);

	const [connectionId, setConnectionId] = React.useState(null);
	const [loggedIn, setLoggedIn] = React.useState(false);
	// Unterscheidet "noch nicht geprueft" von "geprueft, aber nicht eingeloggt" -
	// ohne das wuerde bei jedem Reload kurz die Login-Maske aufblitzen, obwohl
	// der comdirect-Token in $_SESSION serverseitig oft noch bis zu ~10 Minuten
	// gueltig ist (siehe [[project_banking_module]]).
	const [sessionChecked, setSessionChecked] = React.useState(false);
	const [depots, setDepots] = React.useState(null);
	const [error, setError] = React.useState('');
	// null = unsortiert (Reihenfolge der Bank). Klick auf dieselbe Spalte dreht
	// nur die Richtung um, Klick auf eine andere Spalte setzt neu auf aufsteigend.
	const [sortKey, setSortKey] = React.useState(null);
	const [sortDir, setSortDir] = React.useState(1);
	const [orderTarget, setOrderTarget] = React.useState(null);

	function toggleSort(key) {
		if (sortKey === key) {
			setSortDir((current) => -current);
		} else {
			setSortKey(key);
			setSortDir(1);
		}
	}

	function sortArrow(key) {
		if (sortKey !== key) return null;
		return <i className={`bi ${sortDir === 1 ? 'bi-caret-up-fill' : 'bi-caret-down-fill'} brokerage-sort-icon`}></i>;
	}

	function positionName(position) {
		return position.name
			|| (position.instrument && (position.instrument.name || position.instrument.wkn))
			|| position.wkn || position.instrumentId || 'Position';
	}

	function positionQuantity(position) {
		return parseFloat((position.quantity && position.quantity.value) || position.quantity || 0);
	}

	function positionValue(position) {
		return parseFloat((position.currentValue && position.currentValue.value) || 0);
	}

	function sortedPositions(positions) {
		if (sortKey === null) return positions;
		const extract = sortKey === 'name' ? positionName : (sortKey === 'quantity' ? positionQuantity : positionValue);
		return [...positions].sort((a, b) => {
			const valueA = extract(a);
			const valueB = extract(b);
			return (typeof valueA === 'string' ? valueA.localeCompare(valueB) : valueA - valueB) * sortDir;
		});
	}

	// Erste Verbindung automatisch vorauswaehlen, statt eine unnoetige Auswahl
	// zu erzwingen - umschalten auf eine andere geht danach jederzeit ueber den
	// Verbindungs-Wechsler (switchConnection()).
	React.useEffect(() => {
		if (connectionId === null && connections && connections.length > 0) {
			setConnectionId(connections[0].connectionid);
		}
	}, [connections, connectionId]);

	// Zwischen Verbindungen wechseln, ohne eine bereits gueltige comdirect-
	// Sitzung der ZIEL-Verbindung zu verwerfen: der Sitzungs-Check in
	// loadDepots() (ausgeloest ueber die connectionId-Abhaengigkeit unten)
	// erkennt selbst, ob dort noch ein gueltiger Token liegt - hier wird nur
	// der lokale Anzeigezustand zurueckgesetzt, nicht die Sitzung selbst
	// (die lebt ohnehin serverseitig in $_SESSION, pro Verbindung getrennt).
	function switchConnection(id) {
		if (id === connectionId) return;
		setSessionChecked(false);
		setLoggedIn(false);
		setDepots(null);
		setError('');
		setConnectionId(id);
	}

	function loadDepots() {
		x.was.axPost('Backend/webapp.php?module=Banking&task=BrokerageDepotList', { connectionId }, (result) => {
			const content = result.data.content || {};
			if (result.data.returncode !== 1) {
				setSessionChecked(true);
				if (content.status === 'login-required') {
					setLoggedIn(false);
					return;
				}
				setError(content.message || 'Depot konnte nicht geladen werden.');
				return;
			}
			setSessionChecked(true);
			setLoggedIn(true);
			setDepots(content.depots || []);
		});
	}

	function openOrder(depot, position, side) {
		setOrderTarget({
			depotId: depot.depotId,
			settlementAccountId: depot.settlementAccountId,
			side,
			instrumentId: position.instrumentId || position.wkn || '',
			instrumentLabel: positionName(position),
			instrumentIsin: position.isin || null,
			// Immer mit der gehaltenen Stueckzahl vorbelegen (Nutzerwunsch), bleibt
			// natuerlich editierbar - fuer Kauf wie Verkauf gleichermassen.
			quantity: String((position.quantity && position.quantity.value) || position.quantity || ''),
		});
	}

	// Beim (erneuten) Setzen der Verbindung erst prüfen, ob am Server noch eine
	// gueltige comdirect-Sitzung liegt, statt direkt die Login-Maske zu zeigen -
	// loadDepots() setzt loggedIn/sessionChecked je nach Ergebnis selbst.
	React.useEffect(() => {
		if (connectionId !== null) loadDepots();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [connectionId]);

	// Nach erfolgreichem Login (BrokerageLoginForm) direkt nachladen.
	React.useEffect(() => {
		if (loggedIn && depots === null) loadDepots();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [loggedIn]);

	// Haelt die comdirect-Sitzung per refresh_token am Leben, solange diese
	// Kachel offen bleibt (auch im Hintergrund, wenn z.B. die Live Watchlist
	// aktiv angezeigt wird - Module werden in dieser App nur per CSS
	// ausgeblendet, nicht unmounted). Alle 8 Minuten, knapp vor Ablauf des
	// ~10-Minuten-Tokens. Stoppt automatisch, wenn die Kachel wirklich
	// geschlossen wird (Cleanup-Funktion) oder die Verbindung wechselt.
	React.useEffect(() => {
		if (!loggedIn || connectionId === null) return;
		const intervalId = setInterval(() => {
			x.was.axPost('Backend/webapp.php?module=Banking&task=BrokerageRefreshSession', { connectionId }, (result) => {
				if (result.data.returncode !== 1) {
					setLoggedIn(false);
					setSessionChecked(true);
				}
			});
		}, 8 * 60 * 1000);
		return () => clearInterval(intervalId);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [loggedIn, connectionId]);

	const connection = connections ? connections.find((c) => c.connectionid === connectionId) : null;

	let inner;
	if (connections === null) {
		inner = <p>Lade…</p>;
	} else if (connections.length === 0) {
		inner = <p>Keine comdirect-Verbindung vorhanden. Bitte zuerst unter Banking → Konten eine comdirect-Verbindung anlegen.</p>;
	} else if (connectionId === null) {
		inner = (
			<div className="brokerage-picker">
				{connections.map((c) => (
					<div key={c.connectionid} className="brokerage-picker-item" onClick={() => setConnectionId(c.connectionid)}>
						{c.bankname} – {c.accountlabel}
					</div>
				))}
			</div>
		);
	} else if (!sessionChecked) {
		inner = <p>Prüfe Sitzung…</p>;
	} else if (!loggedIn) {
		inner = (
			<BrokerageLoginForm
				x={x}
				connectionId={connectionId}
				accountLabel={connection ? connection.bankname + ' – ' + connection.accountlabel : ''}
				pinUse={true}
				pinStored={connection ? parseInt(connection.pinstored, 10) === 1 : false}
				onLoggedIn={() => setLoggedIn(true)}
			/>
		);
	} else {
		inner = (
			<React.Fragment>
				{error !== '' && <p className="brokerage-error"><i className="bi bi-exclamation-triangle"></i> {error}</p>}
				{depots === null && <p>Lade Depot…</p>}
				{depots !== null && depots.length === 0 && <p>Kein Depot gefunden.</p>}
				{(depots || []).map((depot) => {
					const totalValue = depot.currentValue && depot.currentValue.value;
					return (
						<div key={depot.depotId} className="brokerage-depot">
							<div className="brokerage-depot-header">
								<div>
									<strong>Depot {depot.depotDisplayId}</strong>
									{connection && <span className="brokerage-hint"> – Inhaber: {connection.accountlabel}</span>}
								</div>
								{totalValue && <strong>{totalValue} {depot.currentValue.unit || 'EUR'}</strong>}
							</div>
							{(depot.positions || []).length === 0 && <p className="brokerage-hint">Keine Positionen.</p>}
							{(depot.positions || []).length > 0 &&
								<div className="brokerage-position brokerage-position-header">
									<span className="brokerage-sortable" onClick={() => toggleSort('name')}>Wertpapier{sortArrow('name')}</span>
									<span>WKN</span>
									<span className="brokerage-sortable" onClick={() => toggleSort('quantity')}>Stückzahl{sortArrow('quantity')}</span>
									<span className="brokerage-sortable" onClick={() => toggleSort('value')}>Wert{sortArrow('value')}</span>
									<span></span>
								</div>
							}
							{sortedPositions(depot.positions || []).map((position, idx) => (
								<div
									key={idx}
									className="brokerage-position brokerage-position-clickable"
									onClick={() => openOrder(depot, position, 'SELL')}
								>
									<span>{positionName(position)}</span>
									<span>{position.wkn || ''}</span>
									<span>{(position.quantity && position.quantity.value) || position.quantity || ''} Stk.</span>
									<span>
										{position.currentValue && position.currentValue.value
											? position.currentValue.value + ' ' + (position.currentValue.unit || 'EUR')
											: ''}
									</span>
									<span className="brokerage-position-actions">
										{position.isin &&
											<img
												src="/comdirect-favicon.ico"
												alt="comdirect"
												className="brokerage-action-icon brokerage-action-external"
												title="Bei comdirect ansehen"
												onClick={(e) => { e.stopPropagation(); window.open('https://www.comdirect.de/inf/aktien/' + encodeURIComponent(position.isin), '_blank', 'noopener,noreferrer'); }}
											/>
										}
										<i
											className="bi bi-plus-circle brokerage-action-icon brokerage-action-buy"
											title="Kaufen"
											onClick={(e) => { e.stopPropagation(); openOrder(depot, position, 'BUY'); }}
										></i>
										<i
											className="bi bi-dash-circle brokerage-action-icon brokerage-action-sell"
											title="Verkaufen"
											onClick={(e) => { e.stopPropagation(); openOrder(depot, position, 'SELL'); }}
										></i>
									</span>
								</div>
							))}
						</div>
					);
				})}
			</React.Fragment>
		);
	}

	return (
		<Module id={props.id} x={x} classes="module-brokerage-overview" title={props.title} display="d-block">
			{connections && connections.length > 1 &&
				<div className="brokerage-connection-switch">
					<label htmlFor="brokerage-connection-select">Verbindung</label>
					<select
						id="brokerage-connection-select"
						value={connectionId || ''}
						onChange={(e) => switchConnection(parseInt(e.target.value, 10))}
					>
						{connections.map((c) => (
							<option key={c.connectionid} value={c.connectionid}>{c.bankname} – {c.accountlabel}</option>
						))}
					</select>
				</div>
			}
			{inner}
			{orderTarget &&
				<div className="brokerage-overlay">
					<BrokerageOrderForm
						x={x}
						connectionId={connectionId}
						instrumentLabel={orderTarget.instrumentLabel}
						instrumentIsin={orderTarget.instrumentIsin}
						initialOrder={{
							depotId: orderTarget.depotId,
							settlementAccountId: orderTarget.settlementAccountId,
							instrumentId: orderTarget.instrumentId,
							venueId: '',
							side: orderTarget.side,
							quantity: orderTarget.quantity,
							limitPrice: '',
						}}
						onCancel={() => setOrderTarget(null)}
						onDone={() => { setOrderTarget(null); loadDepots(); }}
					/>
				</div>
			}
		</Module>
	)

};
